const ADDON_DISPLAY = {
  'insured-photographer': { title: 'Insured Photographer', price: 395 },
  'engagement-session': { title: 'Engagement Session', price: 750 },
  'rush-delivery': { title: 'Rush Delivery', price: 650 },
  'raw-footage': { title: 'Raw Footage', price: 395 },
  'liability-insurance': { title: 'Liability Insurance', price: 395 },
  'sneak-peek-photos': { title: 'Sneak Peek Photos', price: 195 },
  'second-photographer': { title: 'Second Photographer', price: 750 },
  'second-shooter': { title: 'Second Photographer', price: 750 },
  'getting-ready-photos': { title: 'Getting Ready Photos', price: 395 },
  'date-change': { title: 'Date Change (after 24 hours)', price: 495 },
  'date-change-flexibility': { title: 'One-Time Date Change', price: 195 },
  'video-coverage': { title: 'Video Coverage', price: 700 }
}

export function normalizeAddonCode(addonCode) {
  return String(addonCode || '').trim().replace(/_/g, '-').toLowerCase()
}

function getAddonDisplay(addonCode) {
  return ADDON_DISPLAY[normalizeAddonCode(addonCode)] || null
}

function normalizeAddon(addon, index, source = 'personalization') {
  const rawCode = typeof addon === 'string' ? addon : addon?.id || addon?.code || addon?.addon_id
  const code = normalizeAddonCode(rawCode)
  const display = getAddonDisplay(code)
  const title = typeof addon === 'string'
    ? display?.title || code
    : addon?.title || addon?.name || display?.title || code || 'Add-on'

  return {
    ...(typeof addon === 'object' && addon ? addon : {}),
    id: typeof addon === 'object' && addon?.id ? addon.id : code || `${source}-${index}`,
    code: typeof addon === 'object' && addon?.code ? addon.code : code,
    title,
    name: typeof addon === 'object' && addon?.name ? addon.name : title,
    price: typeof addon === 'object' && addon?.price !== undefined ? addon.price : display?.price || 0,
    qty: typeof addon === 'object' && addon?.qty ? addon.qty : 1,
    source: typeof addon === 'object' && addon?.source ? addon.source : source,
    isFree: Boolean(typeof addon === 'object' && addon?.isFree)
  }
}

function buildFreePromotionAddOn(addonCode, promoCode) {
  const code = normalizeAddonCode(addonCode)
  const display = getAddonDisplay(code)

  return {
    id: code,
    code,
    title: display?.title || code,
    name: display?.title || code,
    price: display?.price || 0,
    qty: 1,
    isFree: true,
    source: 'promotion',
    promoCode
  }
}

export function mergePromotionFreeAddOns(addons = [], promotion = {}) {
  const mergedAddOns = Array.isArray(addons)
    ? addons.map(addon => (typeof addon === 'object' && addon ? { ...addon } : addon))
    : []
  const freeAddOns = Array.isArray(promotion?.free_addons) ? promotion.free_addons : []

  freeAddOns.forEach((addonCode) => {
    const code = normalizeAddonCode(addonCode)
    if (!code) return

    const existingAddOn = mergedAddOns.find((addon) => {
      const existingCode = typeof addon === 'string'
        ? addon
        : addon?.id || addon?.code || addon?.addon_id
      return normalizeAddonCode(existingCode) === code
    })

    if (existingAddOn && typeof existingAddOn === 'object') {
      const display = getAddonDisplay(code)
      existingAddOn.id = existingAddOn.id || code
      existingAddOn.code = existingAddOn.code || code
      existingAddOn.title = existingAddOn.title || existingAddOn.name || display?.title || code
      existingAddOn.name = existingAddOn.name || existingAddOn.title
      existingAddOn.price = existingAddOn.price !== undefined ? existingAddOn.price : display?.price || 0
      existingAddOn.qty = existingAddOn.qty || 1
      existingAddOn.isFree = true
      existingAddOn.source = existingAddOn.source || 'promotion'
      existingAddOn.promoCode = existingAddOn.promoCode || promotion?.code
      return
    }

    if (existingAddOn && typeof existingAddOn === 'string') {
      const index = mergedAddOns.indexOf(existingAddOn)
      mergedAddOns[index] = buildFreePromotionAddOn(code, promotion?.code)
      return
    }

    mergedAddOns.push(buildFreePromotionAddOn(code, promotion?.code))
  })

  return mergedAddOns
}

export function getInitialBookingAddOns(personalizationData = {}, directAddOns = []) {
  const addonsMap = new Map()
  const personalizationAddOns = Array.isArray(personalizationData?.addons)
    ? personalizationData.addons
    : []
  const fallbackAddOns = Array.isArray(directAddOns) ? directAddOns : []

  ;[...personalizationAddOns, ...fallbackAddOns].forEach((addon, index) => {
    const normalized = normalizeAddon(addon, index)
    const key = normalizeAddonCode(normalized.code || normalized.id) || normalized.id
    if (!addonsMap.has(key)) addonsMap.set(key, normalized)
  })

  const promotion = personalizationData?.promotion
  const freeAddOns = Array.isArray(promotion?.free_addons) ? promotion.free_addons : []
  freeAddOns.forEach((addonCode) => {
    const code = normalizeAddonCode(addonCode)
    if (!code) return

    const existingAddOn = addonsMap.get(code)
    if (existingAddOn) {
      addonsMap.set(code, {
        ...existingAddOn,
        isFree: true,
        source: existingAddOn.source || 'promotion',
        promoCode: existingAddOn.promoCode || promotion?.code
      })
      return
    }

    const display = getAddonDisplay(code)
    addonsMap.set(code, {
      id: code,
      code,
      title: display?.title || code,
      name: display?.title || code,
      price: display?.price || 0,
      qty: 1,
      source: 'promotion',
      isFree: true,
      promoCode: promotion?.code
    })
  })

  return Array.from(addonsMap.values())
}

export function buildGettingReadyAddOn(promoCode = 'GETREADY') {
  return buildFreePromotionAddOn('getting-ready-photos', promoCode)
}

/**
 * True for personalization_data.addons entries that mirror a POST-booking
 * purchase (amendment / portal). Those purchases are the source of truth in
 * booking_addons; pages that list booking_addons separately must exclude the
 * mirror or they count the money twice.
 */
export function isPostBookingAddOn(addon) {
  if (!addon || typeof addon !== 'object') return false
  if (addon.amendment_id) return true
  const source = String(addon.source || '').toLowerCase()
  // 'admin' = recorded by staff from the Billing workbench; 'date-change' = a paid post-booking date change.
  // Both have a booking_addons row, which is where the timeline and the ledger read them.
  return source === 'amendment' || source === 'portal_immediate' || source === 'portal_added_to_balance' || source === 'admin' || source === 'date-change'
}
